import { useCallback, useEffect, useRef, useState } from 'react';
import AxiosFactory from '../../services/http.service';
import ApiRoutes from '../../config/api.routes';
import useMetaMask from '../../contexts/useMetaMask';
import { DocumentType, IpfsDocument, SigningMethod } from './ipfsDocumentTypes';
import { WetInkUploadOutcome, parseWetInkUploadError } from './useWetInkSigner';

interface UseWetInkFlowOptions {
  /** The document type to create — hardcoded per flow (e.g. a covenant/deed). */
  type: DocumentType;
  /** Optional pre-filled template variable values, keyed by placeholder token (e.g. `TOKEN_ID`). */
  templateVars?: Record<string, string>;
  /**
   * Resume an existing document (one this caller created earlier) instead of creating a new one on
   * the first download. For flows where the paper signing happens offline between the download and
   * the upload, so the host has to find its way back to the same document. Omit for create-on-download.
   */
  documentId?: number | null;
}

/**
 * Drives the self-serve wet-ink flow for a logged-in verifier who is the document's sole signer:
 * the first download lazily creates the document (of a fixed `type`, with the verifier as its only
 * WET_INK signer) and downloads the exact PDF to print; the verifier hand-signs it and uploads the
 * scan for automated verification. No signer selection or delegation — the caller is always the
 * signer, so `signerWallet` is never sent (wet-ink guide §3, §4).
 */
export function useWetInkFlow({ type, templateVars, documentId: resumeId }: UseWetInkFlowOptions) {
  const { currentAccount } = useMetaMask();

  // Null until the first download creates the document; reused for every subsequent call.
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [document, setDocument] = useState<IpfsDocument | null>(null);

  const refreshDocument = useCallback(async (id?: number | null): Promise<IpfsDocument | null> => {
    const target = id ?? documentId;
    if (target == null) {return null;}
    const { axios } = new AxiosFactory();
    const res = await axios.get<IpfsDocument>(ApiRoutes.ipfsDocument.get(target));
    setDocument(res.data);
    return res.data;
  }, [documentId]);

  // Adopt a resumed document once the host has found it. Only ever fills an empty slot — a document
  // this hook already created (or adopted) is never swapped out from under an in-flight download.
  useEffect(() => {
    if (resumeId == null || documentId != null) {return;}
    setDocumentId(resumeId);
    // A failed read only leaves `document` empty; download and upload act on the id regardless.
    refreshDocument(resumeId).catch(() => null);
  }, [resumeId, documentId, refreshDocument]);

  // The exact reference PDF fetched from download-for-signing, exposed as a local blob URL.
  const [referenceUrl, setReferenceUrl] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const blobUrlRef = useRef('');

  const [uploadLoading, setUploadLoading] = useState(false);
  const [outcome, setOutcome] = useState<WetInkUploadOutcome | null>(null);

  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    },
    []
  );

  /**
   * Create-on-first-click, then fetch the exact document to print. Re-downloading reuses the same
   * document (never creates a second) — the reference can still change if the template was edited,
   * so always re-download before re-signing (wet-ink guide §5). Triggers a browser download so the
   * verifier has a copy to print and hand-sign.
   */
  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const { axios } = new AxiosFactory();

      let id = documentId;
      if (id == null) {
        const creator = currentAccount.trim();
        if (!creator) {
          setDownloadError('Connect your wallet to create the document.');
          return;
        }
        const createRes = await axios.post<IpfsDocument>(ApiRoutes.ipfsDocument.create, {
          type,
          templateVars: templateVars ?? {},
          requiredSigners: [creator],
          signerMethods: { [creator]: SigningMethod.WET_INK },
        });
        id = createRes.data.id;
        setDocumentId(id);
        setDocument(createRes.data);
      }

      const res = await axios.get<ArrayBuffer>(ApiRoutes.ipfsDocument.downloadForSigning(id), {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) {URL.revokeObjectURL(blobUrlRef.current);}
      blobUrlRef.current = url;
      setReferenceUrl(url);

      const a = window.document.createElement('a');
      a.href = url;
      a.download = `document-${id}-to-sign.pdf`;
      a.click();
    } catch (e: any) {
      setDownloadError(
        e?.response?.data?.message || e?.message || 'Failed to prepare the document for signing.'
      );
    } finally {
      setDownloadLoading(false);
    }
  }, [documentId, currentAccount, type, templateVars]);

  /** Upload the scanned, hand-signed file (multipart field `scan`) for automated verification. */
  const handleUpload = useCallback(
    async (scan: File): Promise<WetInkUploadOutcome> => {
      if (documentId == null) {
        const result: WetInkUploadOutcome = {
          kind: 'error',
          message: 'Download the document before uploading a signed scan.',
        };
        setOutcome(result);
        return result;
      }
      setUploadLoading(true);
      setOutcome(null);
      try {
        const form = new FormData();
        form.append('scan', scan);

        const { axios } = new AxiosFactory();
        const res = await axios.post<IpfsDocument>(
          ApiRoutes.ipfsDocument.wetInkSignature(documentId),
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setDocument(res.data);
        const result: WetInkUploadOutcome = { kind: 'success', document: res.data };
        setOutcome(result);
        return result;
      } catch (e: any) {
        const result = parseWetInkUploadError(e);
        setOutcome(result);
        return result;
      } finally {
        setUploadLoading(false);
      }
    },
    [documentId]
  );

  const clearOutcome = useCallback(() => setOutcome(null), []);

  const isCompleted = document?.status === 'completed';

  return {
    documentId,
    document,
    isCompleted,
    referenceUrl,
    downloadLoading,
    downloadError,
    handleDownload,
    uploadLoading,
    outcome,
    handleUpload,
    clearOutcome,
    refreshDocument,
  };
}
